'use client';

import { useCallback } from 'react';
import { useStore } from '@/app/providers/store';
import type {
  CreationInfo,
  CreationWithTask,
  FormImagePrompt,
  ImagePrompt,
  ToolTaskFormVideoPrompt,
  VideoPrompt,
} from '@/app/types';
import { getResourceUrl, reportEvent } from '@/app/utils';
import { openInNewTab } from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';

export interface UpscaleNavigationOptions {
  /** 上报参数，会合并到 reportEvent */
  reportParams?: Record<string, unknown>;
  /** 任务 ID，用于上报 */
  taskId?: string;
  /** 交互位置，用于上报 */
  interactionPosition?: string;
  /** 追踪 ID，用于上报 */
  traceId?: string;
  /** 是否在新窗口打开 */
  openInNewTab?: boolean;
}

type CreationForUpscale = CreationInfo | CreationWithTask;

/**
 * 根据 creation 类型跳转到图片超分或视频超分页面，并填充表单数据
 * 用于统一处理 TaskCard、CreationDetail、ImageCreation 等处的超分逻辑
 */
export function useUpscaleNavigation() {
  const router = useRouter();
  const { updateUpscaleImageFormParams, updateUpscaleVideoFormParams, upscaleVideoSettings } = useStore((state) => ({
    updateUpscaleImageFormParams: state.updateUpscaleImageFormParams,
    updateUpscaleVideoFormParams: state.updateUpscaleVideoFormParams,
    upscaleVideoSettings: state.upscaleVideoFormParams.settings,
  }));
  const pathname = usePathname();

  const navigateToUpscale = useCallback(
    (creation: CreationForUpscale | undefined, options?: UpscaleNavigationOptions) => {
      if (!creation) return;

      const taskId = options?.taskId ?? (creation as CreationWithTask).task?.id ?? '';
      const creationId = creation.id ?? '';

      reportEvent('operation', {
        interaction_type: 'upscale',
        task_id: taskId,
        creation_id: creationId,
        trace_id: options?.traceId ?? (creation as CreationWithTask).traceId ?? '',
        interaction_position: options?.interactionPosition ?? pathname,
        ...options?.reportParams,
      });

      // 构建目标路径
      const basePath = creation.type === 'image' ? '/create/upscale-image' : '/create/upscale-video';

      // 根据 openInNewTab 选项决定打开方式
      if (options?.openInNewTab) {
        const targetUrl = `${basePath}?creationId=${creationId}`;
        openInNewTab(targetUrl);
        return;
      }
      // 填充表单数据到 store
      if (creation.type === 'image') {
        const previewImageUrl = creation.nomark_uri || creation.uri || '';
        const imagePrompt: FormImagePrompt = {
          id: creationId,
          status: 'done' as const,
          prompt: {
            sourceImage: creation.uri || '',
            sourceImagePreview: previewImageUrl,
            selectedImage: creation.uri || '',
            selectedImagePreview: previewImageUrl,
            resolution: creation.resolution,
          } as ImagePrompt & {
            resolution?: {
              width: number;
              height: number;
              resolution: string;
            };
          },
        };
        updateUpscaleImageFormParams('imagePrompts', [imagePrompt]);
        updateUpscaleImageFormParams('creationId', creation.id);
      } else if (creation.type === 'video') {
        const previewVideoUrl = getResourceUrl({
          uri: creation.uri || '',
          noMarkUri: creation.nomark_uri || '',
        });
        const videoPrompt: ToolTaskFormVideoPrompt = {
          id: creationId,
          status: 'done' as const,
          prompt: {
            video: creation.uri || '',
            videoPreview: previewVideoUrl,
            duration: creation.duration,
            coverImage: creation.cover_uri,
            coverImagePreview: creation.cover_uri,
            resolution: creation.resolution,
          } as VideoPrompt & {
            resolution?: {
              width: number;
              height: number;
              resolution: string;
            };
          },
        };
        updateUpscaleVideoFormParams('videoPrompts', [videoPrompt]);
        updateUpscaleVideoFormParams('creationId', creation.id);
        updateUpscaleVideoFormParams('settings', { ...upscaleVideoSettings, duration: creation.duration });
      }
      router.push(basePath);
    },
    [pathname, router, updateUpscaleImageFormParams, updateUpscaleVideoFormParams, upscaleVideoSettings],
  );

  return { navigateToUpscale };
}
